import React, { useContext, useEffect, useRef, useState } from "react";
import { MainWrapper } from "../../styles";
import {
  Button,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { InputField } from "../../../InputField/InputField";
import { useForm } from "react-hook-form";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import PostJobPreview from "./PostJobPreview";
import {
  AddJobOfferDto,
  StateType,
} from "../../../../../interfaces/JobOffers.model";
import { yupResolver } from "@hookform/resolvers/yup";
import { AddJobSchema } from "../../../../../validators/JobOffers.schema";
import { useNavigate } from "react-router-dom";
import { useGetCompanyDescriptionQuery } from "../../../../../rq/hooks/useQueries";
import { AuthContext } from "../../../../../lib/context/AuthContext";
import { useUpdateCompanyDescriptionMutation } from "../../../../../rq/hooks/useJob.mutation";
import { LoaderIcon } from "react-hot-toast";
import { useQueryClient } from "@tanstack/react-query";

const PostJob = () => {
  /**
   * hooks
   */
  const employmentTypeRef = useRef<HTMLDivElement>(null);
  const jobDescriptionRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [enterpriseDescription, setEnterpriseDescription] = useState("");
  const [openJobPreviewModal, setOpenJobPreviewModal] = useState(false);
  const queryClient = useQueryClient();
  const [selectedEmploymentType, setSelectedEmploymentType] = useState<
    string | null
  >(null);

  const {
    register,
    watch,
    setValue,
    handleSubmit,
    reset,
    trigger,
    getValues,
    formState: { errors },
  } = useForm<AddJobOfferDto>({
    defaultValues: {
      employmentTypes: [],
    },
    resolver: yupResolver(AddJobSchema(t)),
  });

  const companyDescription = useGetCompanyDescriptionQuery();
  const {
    mutate: updateCompanyDescription,
    isLoading: isLoadingCompanyDescription,
  } = useUpdateCompanyDescriptionMutation({
    onSuccess: () => {
      queryClient.invalidateQueries();
    },
  });

  useEffect(() => {
    if (companyDescription.data && companyDescription.data.description) {
      setEnterpriseDescription(
        decodeURIComponent(companyDescription.data.description)
      );
    }
  }, [companyDescription.data]);

  /**
   * constants
   */
  const typeOfEmployments = [
    { id: 1, name: "cdd", label: t("cdd") },
    { id: 2, name: "internship", label: t("internship") },
    { id: 3, name: "jobStudent", label: t("job_student") },
  ];

  /**
   * handlers
   */
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSelectedEmploymentType(value);

    // Find the selected employment type and set its ID
    const selectedId = typeOfEmployments.find(
      (item) => item.name === value
    )?.id;
    setValue("employmentTypes", selectedId ? [selectedId] : [], {
      shouldValidate: true,
    });
  };

  const handleDescription = (formProp: any) => (value: string) => {
    if (value === "<p><br></p>" || value === null) {
      setValue(formProp, "");
    } else {
      setValue(formProp, value, { shouldValidate: true });
    }
  };

  const onSubmit = async (data: AddJobOfferDto) => {
    if (await trigger(["title", "jobDescription", "employmentTypes"])) {
      setOpenJobPreviewModal(true);
    }
  };

  console.log('watch("employmentTypes")', watch("employmentTypes"));

  return (
    <>
      <MainWrapper>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div
            id="scrollable-content"
            className="flex justify-between items-center border-b-2 border-b-[#72C2CC] border-solid mb-2"
          >
            <Typography className="text-base font-bold">
              {t("post_a_job")}
            </Typography>
            {watch("employmentTypes") && watch("employmentTypes").length > 0 ? (
              <Typography className="text-[#3F6FE8] text-base">
                {watch("employmentTypes").includes(1)
                  ? t("use_10_credits")
                  : t("use_1_credit")}
              </Typography>
            ) : null}
          </div>
          <div className="flex flex-col gap-2 border-b-2 border-b-[#72C2CC] border-solid">
            <div className="flex flex-col gap-1  ">
              <Typography className="font-bold text-sm">
                {t("job_title")}
              </Typography>
              <Typography className="text-sm text-[#7C8493]">
                {t("job_description")}
              </Typography>
            </div>
            <div className="my-2">
              <InputField
                placeholder={t("job_title")}
                type="input"
                fullWidth
                variant="standard"
                size="small"
                {...register("title")}
                fieldError={errors.title}
                InputProps={{
                  sx: {
                    backgroundColor: "#F4F7F9",
                    borderRadius: "10px",
                    padding: "0 10px",
                  },
                  disableUnderline: true,
                }}
              />
            </div>
          </div>
          <div
            ref={employmentTypeRef}
            className="flex flex-col gap-2 border-b-2 border-b-[#72C2CC] border-solid"
          >
            <div className="flex flex-col gap-1">
              <Typography className="font-bold text-sm">
                {t("type_of_employment")}
              </Typography>
              <Typography className="text-sm text-[#7C8493]">
                {t("type_of_employment_description")}
              </Typography>
            </div>
            <div>
              <RadioGroup
                row
                value={selectedEmploymentType}
                onChange={handleChange}
              >
                {typeOfEmployments.map(({ id, label, name }) => (
                  <FormControlLabel
                    key={id}
                    value={name}
                    control={<Radio />}
                    label={label}
                  />
                ))}
              </RadioGroup>
              {!!errors.employmentTypes && (
                <Typography
                  sx={{ color: "red" }}
                  variant="caption"
                  display="block"
                >
                  {errors.employmentTypes?.message}
                </Typography>
              )}
            </div>
          </div>
          <div className="flex flex-col gap-2 border-b-2 border-b-[#72C2CC] border-solid pb-10 pt-2 ">
            <div className="flex flex-col gap-1">
              <Typography className="font-bold text-sm">
                {t("enterprise_description")}
              </Typography>
              <Typography className="text-sm text-[#7C8493]">
                {t("enterprise_little_description")}
              </Typography>
            </div>
            <div>
              <ReactQuill
                theme="snow"
                value={enterpriseDescription}
                onChange={setEnterpriseDescription}
                modules={{
                  toolbar: [
                    ["bold", "italic"],
                    [{ list: "ordered" }, { list: "bullet" }],
                  ],
                }}
                formats={["bold", "italic", "list", "bullet"]}
              />
            </div>
            <Button
              type="button"
              disabled={enterpriseDescription === user?.description}
              onClick={() => {
                updateCompanyDescription({
                  description: enterpriseDescription,
                });
              }}
              variant="outlined"
              className="w-fit flex gap-2 disabled:cursor-not-allowed"
            >
              <Typography
                className={` text-sm capitalize ${
                  enterpriseDescription === user?.description
                    ? "text-[#A7A7A7]"
                    : "text-[#3F6FE8]"
                }`}
              >
                {t("save_to_profile")}
              </Typography>{" "}
              {isLoadingCompanyDescription && (
                <LoaderIcon className="animate-spin" />
              )}
            </Button>
          </div>
          <div
            ref={jobDescriptionRef}
            className="flex flex-col gap-2 border-b-2 border-b-[#72C2CC] border-solid pb-10 pt-2 "
          >
            <div className="flex flex-col gap-1">
              <Typography className="font-bold text-sm">
                {t("job_description_title")}
              </Typography>
              <Typography className="text-sm text-[#7C8493]">
                {t("job_description_description")}
              </Typography>
            </div>
            <div>
              <ReactQuill
                theme="snow"
                value={watch("jobDescription")}
                onChange={handleDescription("jobDescription")}
                modules={{
                  toolbar: [
                    ["bold", "italic"],
                    [{ list: "ordered" }, { list: "bullet" }],
                  ],
                }}
                formats={["bold", "italic", "list", "bullet"]}
              />
            </div>
            {errors.jobDescription && (
              <Typography
                sx={{ color: "red" }}
                variant="caption"
                display="block"
              >
                {errors.jobDescription.message}
              </Typography>
            )}
          </div>
          <div className="flex flex-col gap-2 border-b-2 border-b-[#72C2CC] border-solid pb-10 pt-2 ">
            <div className="flex flex-col gap-1">
              <Typography className="font-bold text-sm">
                {t("function_title")}
              </Typography>
              <Typography className="text-sm text-[#7C8493]">
                {t("function_description")}
              </Typography>
            </div>
            <div>
              <ReactQuill
                theme="snow"
                value={watch("functionDescription")}
                onChange={handleDescription("functionDescription")}
                modules={{
                  toolbar: [
                    ["bold", "italic"],
                    [{ list: "ordered" }, { list: "bullet" }],
                  ],
                }}
                formats={["bold", "italic", "list", "bullet"]}
              />
            </div>
          </div>
          <div className="flex flex-col gap-2 border-b-2 border-b-[#72C2CC] border-solid pb-10 pt-2 ">
            <div className="flex flex-col gap-1">
              <Typography className="font-bold text-sm">
                {t("offer_title")}
              </Typography>
              <Typography className="text-sm text-[#7C8493]">
                {t("offer_description")}
              </Typography>
            </div>
            <div>
              <ReactQuill
                theme="snow"
                value={watch("offerDescription")}
                onChange={handleDescription("offerDescription")}
                modules={{
                  toolbar: [
                    ["bold", "italic"],
                    [{ list: "ordered" }, { list: "bullet" }],
                  ],
                }}
                formats={["bold", "italic", "list", "bullet"]}
              />
            </div>
          </div>
          <div className="flex flex-col gap-2 border-b-2 border-b-[#72C2CC] border-solid pb-10 pt-2 ">
            <div className="flex flex-col gap-1">
              <Typography className="font-bold text-sm">
                {t("company_website_url")}
              </Typography>
              <Typography className="text-sm text-[#7C8493]">
                {t("company_website_url_description")}
              </Typography>
            </div>
            <InputField
              placeholder={t("company_website_url_placeholder")}
              size="small"
              fullWidth
              {...register("companyWebsiteUrl")}
              error={!!errors.companyWebsiteUrl}
              helperText={errors.companyWebsiteUrl?.message}
            />
          </div>
          <div className="flex flex-col gap-2 border-b-2 border-b-[#72C2CC] border-solid pb-10 pt-2 ">
            <Typography className="font-bold text-sm">
              {t("teaser_title")}
            </Typography>

            <div>
              <ReactQuill
                theme="snow"
                value={watch("teaser")}
                onChange={handleDescription("teaser")}
                modules={{
                  toolbar: [
                    ["bold", "italic"],
                    [{ list: "ordered" }, { list: "bullet" }],
                  ],
                }}
                formats={["bold", "italic", "list", "bullet"]}
              />
            </div>
          </div>
          <div className="flex justify-end gap-1 my-2">
            <Button className="capitalize" onClick={() => navigate(-1)}>
              {t("discard")}
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                if (errors.employmentTypes) {
                  employmentTypeRef.current?.scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                  });
                }
                if (errors.jobDescription) {
                  jobDescriptionRef.current?.scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                  });
                }
              }}
              className="capitalize bg-[#2E98FA]"
              type="submit"
            >
              {t("preview")}
            </Button>
          </div>
        </form>
      </MainWrapper>
      <PostJobPreview
        open={openJobPreviewModal}
        handleClose={() => setOpenJobPreviewModal(false)}
        jobDetails={{
          ...getValues(),
          jobDescription: encodeURIComponent(getValues("jobDescription")),
          enterpriseDescription: encodeURIComponent(enterpriseDescription),
          functionDescription: encodeURIComponent(
            getValues("functionDescription")
          ),
          teaser: encodeURIComponent(getValues("teaser")),
          offerDescription: encodeURIComponent(getValues("offerDescription")),
        }}
      />
    </>
  );
};

export default PostJob;
